import type { MfeVersion, ProductsResponse } from "./types";

const VERSIONED_MFE_NAME_PATTERN = /^(.*)\/(\d+(?:\.\d+)*)$/;

export function getMfeBaseName(name: string): string {
  const match = name.match(VERSIONED_MFE_NAME_PATTERN);
  if (!match) {
    return name;
  }

  return match[1];
}

export function formatMfeDisplayName(name: string): string {
  const match = name.match(VERSIONED_MFE_NAME_PATTERN);
  if (!match) {
    return name;
  }

  return `${match[1]}:${match[2]}`;
}

export function filterMfeNames(
  mfeNames: string[],
  activeProduct: string | null,
  products: ProductsResponse | null,
): string[] {
  if (!activeProduct || !products) return mfeNames;
  const product = products.products[activeProduct];
  if (!product) return mfeNames;
  const allowed = new Set(product.mfes);
  return mfeNames.filter((name) => {
    const baseName = getMfeBaseName(name);
    return allowed.has(name) || allowed.has(baseName);
  });
}

// mfesByEnv is ordered by precedence (dev, stage, prod): a row's major comes from the first env that has it.
function getRowMajor(name: string, mfesByEnv: Record<string, MfeVersion>[]): string | undefined {
  return mfesByEnv.find((mfes) => name in mfes)?.[name].major;
}

// Only numeric majors are filter options: non-version aliases (e.g. bundle tags) stay visible under "All" only.
export function getMfeMajors(mfesByEnv: Record<string, MfeVersion>[]): string[] {
  const names = new Set(mfesByEnv.flatMap((mfes) => Object.keys(mfes)));
  const majors = new Set<string>();
  for (const name of names) majors.add(getRowMajor(name, mfesByEnv)!);
  return [...majors].filter((m) => /^\d+$/.test(m)).sort((a, b) => Number(a) - Number(b));
}

export function filterMfeNamesByMajor(
  mfeNames: string[],
  major: string,
  mfesByEnv: Record<string, MfeVersion>[],
): string[] {
  if (!major) return mfeNames;
  return mfeNames.filter((name) => getRowMajor(name, mfesByEnv) === major);
}

export function formatProductName(name?: string | null): string {
  if (!name) return "";
  return name.replaceAll("-", " ").toUpperCase();
}

export function hasMfeOverlap(
  mfeNames: string[],
  activeProduct: string | null,
  products: ProductsResponse | null,
): boolean {
  if (!activeProduct || !products) return true;
  const product = products.products[activeProduct];
  if (!product) return true;
  const allowed = new Set(product.mfes);
  return mfeNames.some((name) => {
    const baseName = getMfeBaseName(name);
    return allowed.has(name) || allowed.has(baseName);
  });
}

const PRODUCT_LABEL_PREFIX = "product:";

export function getProductLabels(labels: string[] | undefined): string[] {
  if (!labels) return [];
  return labels
    .filter((l) => l.startsWith(PRODUCT_LABEL_PREFIX))
    .map((l) => l.slice(PRODUCT_LABEL_PREFIX.length));
}

export function matchesProductLabel(
  labels: string[] | undefined,
  activeProduct: string | null,
): boolean {
  if (!activeProduct) return true;
  return getProductLabels(labels).includes(activeProduct);
}

export function isProductMfe(
  mfeName: string,
  products: ProductsResponse | null,
): boolean {
  if (!products) return false;
  const baseName = getMfeBaseName(mfeName);
  return baseName in products.products;
}
